import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export interface OptionCardProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className' | 'children'> {
  label: ReactNode;
  /** Secondary line: what this option means. */
  description?: ReactNode;
  /** Right-aligned slot, normally a formatted price delta. */
  meta?: ReactNode;
  /**
   * Leading visual, between the radio glyph and the label — a thumbnail of what
   * the option produces. Decorative by contract: the label already names the
   * option, so this must not be the only way to tell two cards apart.
   */
  media?: ReactNode;
  /**
   * Tighter padding and a smaller glyph, for a list of cards nested inside
   * something else. The touch target stays above 44px — compact means less
   * air around the words, never a control a thumb cannot hit.
   */
  compact?: boolean;
  className?: string;
}

/**
 * A radio rendered as a full-width card — the print-options pattern (paper, sides,
 * colour, binding).
 *
 * It is a real `<input type="radio">` visually hidden inside its `<label>`, not a
 * div with `role="radio"`. That gets arrow-key group navigation, form
 * participation and `:checked` for free, and it cannot fall out of sync with the
 * accessibility tree. The card's selected and focus states are driven off the
 * input with Tailwind's `peer-*` variants, so the visual state is always the
 * control's real state.
 */
export const OptionCard = forwardRef<HTMLInputElement, OptionCardProps>(function OptionCard(
  { label, description, meta, media, compact = false, className, id, name, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const descriptionId = `${fieldId}-description`;

  return (
    <label
      htmlFor={fieldId}
      className={cn('block cursor-pointer', rest.disabled && 'cursor-not-allowed', className)}
    >
      <input
        {...rest}
        ref={ref}
        id={fieldId}
        name={name}
        type="radio"
        aria-describedby={description ? descriptionId : undefined}
        className="peer sr-only"
      />
      <span
        className={cn(
          'flex min-h-touch items-center rounded-card border bg-surface',
          compact ? 'gap-2.5 px-3 py-2' : 'gap-3 p-4',
          'transition-colors duration-150',
          'border-line-strong',
          // Selected: border + tint + ring, never colour alone (WCAG 1.4.1).
          'peer-checked:border-brand-ink peer-checked:bg-brand-soft peer-checked:ring-1 peer-checked:ring-brand-ink',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2',
          'peer-disabled:bg-surface-sunken peer-disabled:text-ink-subtle',
          // The glyph below is a DESCENDANT, not a sibling, so plain `peer-checked:`
          // cannot reach it (peer variants compile to a `~` sibling selector).
          // Arbitrary-variant nesting is the way in: `.peer:checked ~ .this .dot`.
          'peer-checked:[&_[data-radio-ring]]:border-brand-ink',
          'peer-checked:[&_[data-radio-dot]]:bg-brand-ink',
        )}
      >
        {/* Explicit radio glyph: the native input is sr-only, so draw the affordance. */}
        <span
          aria-hidden="true"
          data-radio-ring
          className={cn(
            'grid shrink-0 place-items-center rounded-full border-2 border-line-strong',
            compact ? 'h-4 w-4' : 'h-5 w-5',
          )}
        >
          <span
            data-radio-dot
            className={cn('rounded-full bg-transparent', compact ? 'h-2 w-2' : 'h-2.5 w-2.5')}
          />
        </span>

        {media ? (
          <span aria-hidden="true" className="shrink-0">
            {media}
          </span>
        ) : null}

        <span className="flex min-w-0 flex-1 flex-col">
          <span className={cn('font-medium text-ink', compact && 'text-sm')}>{label}</span>
          {description ? (
            <span
              id={descriptionId}
              className={cn('text-ink-muted', compact ? 'text-xs' : 'text-sm')}
            >
              {description}
            </span>
          ) : null}
        </span>

        {meta ? <span className="shrink-0 text-sm font-semibold text-ink">{meta}</span> : null}
      </span>
    </label>
  );
});

export interface RadioGroupProps {
  /** The group's question, e.g. "Paper size". Rendered as the fieldset legend. */
  legend: string;
  hint?: ReactNode;
  error?: string | undefined;
  className?: string;
  /** Smaller legend and hint, to match a list of compact cards. */
  compact?: boolean;
  children: ReactNode;
}

/**
 * Wraps OptionCards in a fieldset/legend so the group has one accessible name.
 * Without this, a screen reader announces four unrelated radios with no idea what
 * question they answer.
 */
export function RadioGroup({
  legend,
  hint,
  error,
  className,
  compact = false,
  children,
}: RadioGroupProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <fieldset
      className={cn('flex flex-col gap-2', className)}
      aria-describedby={
        [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined
      }
      aria-invalid={error ? true : undefined}
    >
      <legend className={cn('mb-1 font-medium text-ink', compact ? 'text-xs' : 'text-sm')}>
        {legend}
      </legend>
      {hint ? (
        <p id={hintId} className={cn('-mt-1 text-ink-subtle', compact ? 'text-xs' : 'text-sm')}>
          {hint}
        </p>
      ) : null}
      {children}
      {error ? (
        <p id={errorId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
